import React from "react";
import { Waves } from "lucide-react";

const FEATURES = [
  "AI troubleshooting across every layer — CUCM, SIP, CUBE, PRI, QoS & L2/L3",
  "Gated PPDIO migration runbooks — Webex Calling & CUCM 15",
  "Readiness, device compatibility, waves & cutover sign-off",
  "Private workspace — your data stays yours",
];

export const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-zinc-950 text-zinc-100 flex" style={{ paddingTop: "env(safe-area-inset-top)" }}>
    {/* Brand / value panel */}
    <div className="hidden lg:flex lg:w-[46%] flex-col justify-between p-12 border-r border-zinc-800 bg-gradient-to-b from-zinc-900 to-zinc-950 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-blue-600/10 blur-3xl" />
      <div className="flex items-center gap-3.5 relative z-10">
        <img src="/logo-mark.png" alt="UC Engineer Pro" className="h-14 w-14 rounded-lg object-cover ring-1 ring-blue-500/30" />
        <div>
          <div className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-white leading-none">UC Engineer Pro</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1.5">Troubleshoot · Configure · Migrate · Validate</div>
        </div>
      </div>
      <div className="relative z-10 space-y-6">
        <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-600/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300">
          <Waves className="h-3.5 w-3.5" /> AI-Powered UC Engineering Workspace
        </span>
        <h2 className="font-heading text-3xl font-bold leading-tight text-white max-w-sm">
          The AI-powered workspace to troubleshoot, configure, migrate &amp; validate Cisco UC.
        </h2>
        <ul className="space-y-3">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm text-zinc-400">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
              {f}
            </li>
          ))}
        </ul>
      </div>
      <div className="relative z-10 text-[11px] text-zinc-600 font-mono">Try UC Engineer Pro free · no credit card required</div>
    </div>

    {/* Form panel */}
    <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-sm">
        <div className="lg:hidden mb-8">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="UC Engineer Pro" className="h-12 w-12 rounded-md object-cover ring-1 ring-blue-500/30" />
            <span className="font-heading font-extrabold text-white text-2xl sm:text-3xl tracking-tight">UC Engineer Pro</span>
          </div>
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-blue-500/30 bg-blue-600/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-300">
            AI-Powered UC Engineering Workspace
          </span>
        </div>
        <h1 className="font-heading text-2xl font-bold text-white">{title}</h1>
        {subtitle && <p className="text-sm text-zinc-400 mt-1.5">{subtitle}</p>}
        <div className="mt-8">{children}</div>
        <div className="mt-8 pt-5 border-t border-zinc-800/70 flex items-center justify-center gap-4 text-[11px] text-zinc-600">
          <a href="/privacy" className="hover:text-zinc-400">Privacy Policy</a>
          <span className="text-zinc-700">·</span>
          <a href="/terms" className="hover:text-zinc-400">Terms of Service</a>
        </div>
      </div>
    </div>
  </div>
);

export const GoogleButton = ({ label = "Continue with Google" }) => {
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="google-auth-btn"
      className="w-full flex items-center justify-center gap-3 h-11 rounded-lg border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-sm font-medium text-white transition-colors"
    >
      <svg className="h-4 w-4" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
      {label}
    </button>
  );
};

export const AuthField = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs font-medium text-zinc-400">{label}</span>
    <input
      {...props}
      className="mt-1.5 w-full h-11 rounded-lg bg-zinc-900 border border-zinc-700 px-3.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-colors"
    />
  </label>
);

export const AuthError = ({ message }) =>
  message ? (
    <div data-testid="auth-error" className="rounded-lg border border-red-900/50 bg-red-950/40 px-3.5 py-2.5 text-sm text-red-300">
      {message}
    </div>
  ) : null;
